import { Lead } from '../types/LeadTypes';
import { sub, isWithinInterval, startOfDay } from 'date-fns';

// Define a union type for all supported timeframes
export type TimeFrameOption = 'Past 7 days' | 'Past 30 days' | 'Past 90 days' | 'Past year' | 'All time';

// Default timeframe to use when none specified
export const DEFAULT_TIMEFRAME: TimeFrameOption = 'Past 30 days';

/**
 * Gets the start date based on the selected timeframe
 * @param timeFrame The selected time period
 * @returns The start date of the period
 */
export const getTimeFrameStartDate = (timeFrame: TimeFrameOption): Date | null => {
  if (timeFrame === 'All time') {
    return null;
  }

  // Use UTC midnight for 'now' to match ad spend logic
  const now = startOfDay(new Date(Date.UTC(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth(),
    new Date().getUTCDate()
  )));
  
  switch (timeFrame) {
    case 'Past 7 days':
      return sub(now, { days: 7 });
    case 'Past 30 days':
      return sub(now, { days: 30 });
    case 'Past 90 days':
      return sub(now, { days: 90 });
    case 'Past year':
      return sub(now, { years: 1 });
    default:
      return sub(now, { days: 30 }); // Default to 30 days
  }
};

/**
 * Parse a date string in MM/DD/YYYY or MM/DD/YY format.
 * Two-digit years are assumed to be in the 2000s (e.g., "23" becomes 2023).
 * @param dateString Date string in MM/DD/YYYY or MM/DD/YY format
 * @returns JavaScript Date object
 */
export const parseLeadDate = (dateString: string): Date | null => {
  try {
    const datePart = dateString.split(' ')[0];

    let month: number;
    let day: number;
    let year: number;

    if (datePart.includes('/')) {
      // Format MM/DD/YYYY or MM/DD/YY
      const dateParts = datePart.split('/');
      if (dateParts.length !== 3) {
        console.error('Invalid date format:', dateString);
        return null;
      }
      month = parseInt(dateParts[0], 10) - 1;
      day = parseInt(dateParts[1], 10);
      year = parseInt(dateParts[2], 10);
    } else if (datePart.includes('-')) {
      // Format YYYY-MM-DD
      const dateParts = datePart.split('-');
      if (dateParts.length !== 3) {
        console.error('Invalid date format:', dateString);
        return null;
      }
      year = parseInt(dateParts[0], 10);
      month = parseInt(dateParts[1], 10) - 1;
      day = parseInt(dateParts[2], 10);
    } else {
      console.error('Invalid date format:', dateString);
      return null;
    }

    if (year < 100) {
      year += 2000;
    }

    return new Date(year, month, day);
  } catch (error) {
    console.error('Error parsing date:', dateString, error);
    return null;
  }
};

/**
 * Filters leads based on the selected timeframe
 * @param leads Array of leads to filter
 * @param timeFrame Time period to filter by
 * @returns Filtered array of leads
 */
export const filterLeadsByTimeFrame = (leads: Lead[], timeFrame: TimeFrameOption): Lead[] => {
  if (timeFrame === 'All time') {
    return leads;
  }

  const startDate = getTimeFrameStartDate(timeFrame);
  if (!startDate) {
    return leads;
  }
  // Use UTC midnight for 'now' to match ad spend logic
  const now = startOfDay(new Date(Date.UTC(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth(),
    new Date().getUTCDate()
  )));
  
  return leads.filter(lead => {
    const leadDate = parseLeadDate(lead.date);
    if (!leadDate) {
      return false;
    }
    return leadDate >= startDate && leadDate < now;
  });
};

/**
 * Returns an array of all available timeframe options
 * @returns Array of timeframe options
 */
export const getTimeFrameOptions = (): TimeFrameOption[] => {
  return ['Past 7 days', 'Past 30 days', 'Past 90 days', 'Past year', 'All time'];
};

/**
 * Validates if the provided string is a valid TimeFrameOption
 * @param timeFrame String to validate
 * @returns The timeFrame if valid, or DEFAULT_TIMEFRAME if not
 */
export const validateTimeFrame = (timeFrame: string): TimeFrameOption => {
  if (isValidTimeFrame(timeFrame)) {
    return timeFrame;
  }
  return DEFAULT_TIMEFRAME;
};

/**
 * Type guard to check if a string is a valid TimeFrameOption
 */
export const isValidTimeFrame = (timeFrame: string): timeFrame is TimeFrameOption => {
  return getTimeFrameOptions().includes(timeFrame as TimeFrameOption);
};
